<script setup lang="ts">
  import { loadWithStatusAndErrorHandleAsync } from '@/services/loading-svc.ts';
  import { useCalendarDayStore } from '@/stores/calendar-day.ts';
  import { useThemeStore } from '@/stores/theme';
  import dayjs from 'dayjs';
  import { Calendar } from 'v-calendar';
  import type {
    CalendarDay,
    Page,
  } from 'v-calendar/dist/types/src/utils/page.js';
  import { computed, onMounted, ref } from 'vue';

  const calendarDayStore = useCalendarDayStore();
  const themeStore = useThemeStore();

  const loading = ref<boolean>(false);
  const now = ref<dayjs.Dayjs>(dayjs());
  const date = ref<Date>(new Date());
  const savedYearMonth = ref<string>();
  const dateHolidaySet = ref<Set<string>>(new Set<string>());

  const emit = defineEmits<{
    (e: 'on-change', item: dayjs.Dayjs): void;
  }>();

  const attributes = computed(() => {
    return [
      {
        key: 'select',
        highlight: true,
        dates: [date.value],
      },
    ];
  });

  async function refreshAsync(yearMonth: string) {
    if (savedYearMonth.value == yearMonth) return;
    savedYearMonth.value = yearMonth;
    await loadWithStatusAndErrorHandleAsync(
      (status) => {
        loading.value = status;
      },
      async () => {
        (await calendarDayStore.listAsync(yearMonth)).forEach((item) => {
          if (item.holiday) dateHolidaySet.value.add(item.date);
        });
      }
    );
  }

  function onDayClick(day: CalendarDay) {
    if (day.isDisabled) return;
    date.value = day.noonDate;
    emit('on-change', dayjs(date.value));
  }

  function updatePage(pages: Page[]) {
    if (!pages) return;
    refreshAsync(pages[0].id);
  }

  onMounted(() => {
    refreshAsync(dayjs().format('YYYY-MM'));
  });
</script>
<template>
  <div class="flex justify-center items-center">
    <Calendar
      :is-dark="themeStore.isDark"
      :is-loading="loading"
      :style="{
        width: '100%',
        backgroundColor: themeStore.isDark ? '#2A323C' : '#fff',
      }"
      :attributes="attributes"
      :min-date="new Date('2023-10-01')"
      :max-date="new Date()"
      :trim-weeks="true"
      :masks="{ title: 'YYYY년 M월' }"
      @update:pages="updatePage"
      @dayclick="onDayClick">
      <template #header-title="{ title }">
        <div>
          {{ title }}
        </div>
      </template>

      <template #day-content="{ day }">
        <div class="vc-day vc-day-box-center-center c-day">
          <div
            class="vc-day-content vc-focus vc-attr vc-disabled vc-attr"
            tabindex="-1"
            role="button"
            @click="onDayClick(day)">
            <div
              v-if="dateHolidaySet.has(day.id)"
              class="vc-attr vc-content vc-red">
              {{ day.day }}
            </div>
            <div
              v-else-if="dayjs(day.id).day() == 6"
              class="vc-attr vc-content vc-blue">
              {{ day.day }}
            </div>
            <div
              v-else-if="dayjs(day.id).isAfter(now)"
              class="vc-attr vc-content c-next-day">
              {{ day.day }}
            </div>
            <div
              v-else-if="dayjs(date).format('YYYY-MM-DD') == day.id"
              class="vc-attr vc-content"
              style="color: #ffffff">
              {{ day.day }}
            </div>
            <div
              v-else
              class="vc-attr vc-content vc-gray">
              {{ day.day }}
            </div>
          </div>
        </div>
      </template>
    </Calendar>
    <div class="absolute">
      <span
        v-if="loading"
        class="loading loading-ring loading-lg" />
    </div>
  </div>
</template>
<style scoped lang="scss">
  .vc-red,
  .vc-blue {
    font-weight: normal !important;
  }

  .vc-gray {
    [data-theme='dark'] & {
      color: #999 !important;
    }
  }
  .c-day {
    .c-next-day {
      font-weight: normal !important;
      color: #94a3b8;
      [data-theme='dark'] & {
        color: #777;
      }
    }
  }
</style>
